import { useMemo } from 'react';
import { T, useGT } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useEmailTemplates,
  useEmailTemplateBuiltins,
} from 'src/api/rpc/hooks/communications/useEmailTemplates';
import { TemplateLinkButton } from './TemplateLinkButton';

/** Sentinel select value for the "no template" option (clears the field). */
const NONE_VALUE = '__none__';

/**
 * Email template picker shared by the workflow config panels and the ad-hoc
 * Notify dialog. Values are `builtin:<key>` refs or org template ids — both
 * are URL segments of the template page, so a selected template gets a
 * "view in new tab" button next to the select without any lookup.
 */
export function EmailTemplateSelectWithLink({
  id,
  value,
  onChange,
  noneLabel,
  includeBuiltins = true,
  showInactive = false,
  triggerClassName,
  placeholder,
}: {
  id?: string;
  /** Empty string = nothing selected. */
  value: string;
  /** `undefined` when the "none" option is picked. */
  onChange: (value: string | undefined) => void;
  /** Label for the clearing option; omit to make a selection required. */
  noneLabel?: string;
  includeBuiltins?: boolean;
  /** Mark inactive org templates instead of hiding them. */
  showInactive?: boolean;
  triggerClassName?: string;
  placeholder?: string;
}) {
  const gt = useGT();
  const { data: builtinsData } = useEmailTemplateBuiltins();
  const { data: templatesData } = useEmailTemplates();
  const builtinTemplates = useMemo(
    () => (includeBuiltins ? (builtinsData?.builtins ?? []) : []),
    [includeBuiltins, builtinsData],
  );
  const orgTemplates = useMemo(
    () =>
      (templatesData?.templates ?? []).filter(
        (t) => showInactive || t.isActive || t.id === value,
      ),
    [templatesData, showInactive, value],
  );

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <Select
          value={value || (noneLabel ? NONE_VALUE : undefined)}
          onValueChange={(v) => onChange(v === NONE_VALUE ? undefined : v)}
        >
          <SelectTrigger id={id} className={triggerClassName}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {noneLabel ? (
              <SelectItem value={NONE_VALUE}>{noneLabel}</SelectItem>
            ) : null}
            {builtinTemplates.map((b) => (
              <SelectItem key={b.key} value={`builtin:${b.key}`}>
                {b.name}
                {gt(' (built-in)', {
                  context: 'built-in email template suffix',
                })}
              </SelectItem>
            ))}
            {orgTemplates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {!t.isActive && (
                  <span className="text-muted-foreground ml-1">
                    <T context="inactive email template suffix">(inactive)</T>
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {value ? <TemplateLinkButton templateRef={value} /> : null}
    </div>
  );
}
